// ============================================================================
// CLEO'S SECRET — MagicLinkLogin
// File: src/pages/MagicLinkLogin.jsx
//
// Returning-user sign-in. Sends a Supabase magic link to the patient's email.
// Two states:
//   1. Form state    — email input + send button
//   2. Sent state    — confirmation that the email is on its way
//
// Single-screen, centered. Same editorial pattern as EmailGate:
//   • AnkhMed brand mark, h1 (roman + italic), gold accent rule, body
//   • PAP input on SAND page, 1px SAND3 border, focus → BR
//   • Cinzel uppercase labels at weight 700; BtnPrimary large submit
//   • Inline validation error in italic Cormorant (not a tiny red eyebrow)
//   • Side link to intake for new users (italic underlined BR)
// ============================================================================

import React, { useState } from 'react';
import { signInWithMagicLink } from '../lib/supabase-browser';
import {
  FF, P, T,
  BtnPrimary,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Page shell (shared by both states) ─────────
// Hoisted to module scope so its function reference is stable across
// MagicLinkLogin re-renders. When defined inside the component body, every
// keystroke that calls setEmail produced a fresh Shell reference, which
// React's reconciler treated as a different component type — unmounting
// the whole subtree (including the email input) and dropping focus.
function Shell(props) {
  return (
    <div style={{
      fontFamily: FF,
      background: P.SAND,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 80px) clamp(20px, 4vw, 48px)',
    }}>
      <div style={{
        maxWidth: 600,
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {props.children}
      </div>
    </div>
  );
}

export default function MagicLinkLogin(props) {
  var onNavigate = props.onNavigate;
  // returnTo is set when the user is deep-linking to a specific page and
  // got bounced here for sign-in (e.g. ProgramDashboard's "Sign in" CTA
  // navigates with { returnTo: 'program' }). Passed to the send endpoint
  // so the email's verify URL carries &return=program; AuthVerify reads
  // it and routes there after success.
  var routePayload = props.routePayload || {};
  var returnTo = routePayload.returnTo || null;

  var emailState = useState('');
  var email = emailState[0];
  var setEmail = emailState[1];

  var sendingState = useState(false);
  var sending = sendingState[0];
  var setSending = sendingState[1];

  var sentState = useState(false);
  var sent = sentState[0];
  var setSent = sentState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(null);

    var cleanEmail = email.trim().toLowerCase();
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSending(true);
    signInWithMagicLink(cleanEmail, returnTo)
      .then(function() {
        setSent(true);
        setSending(false);
      })
      .catch(function(err) {
        setError((err && err.message) || 'Could not send magic link. Please try again.');
        setSending(false);
      });
  }

  function goIntake() {
    if (onNavigate) onNavigate('intake');
  }

  // ───────── Shared title styles ─────────
  var titleStyle = {
    fontFamily: FF,
    fontSize: 'clamp(36px, 4.8vw, 60px)',
    fontWeight: 400,
    lineHeight: 1.08,
    letterSpacing: '-0.018em',
    color: P.INK,
    margin: '0 0 32px',
    maxWidth: 600,
  };
  var accentRule = {
    width: 'clamp(140px, 18vw, 220px)',
    height: 2,
    background: P.GO,
    opacity: 0.9,
    margin: '0 0 clamp(28px, 3vw, 40px)',
  };

  // ═════════════════════════════════════════════════════════════════════
  // SENT STATE — magic link sent confirmation
  // ═════════════════════════════════════════════════════════════════════
  if (sent) {
    return (
      <Shell>
        <div style={{ marginBottom: 'clamp(24px, 2.5vw, 36px)' }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>

        <h1 style={titleStyle}>
          <span style={{ display: 'block' }}>Your magic link</span>
          <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>is on its way.</span>
        </h1>
        <div style={accentRule} />

        <p style={{
          ...T.bodyLg,
          color: P.TX,
          maxWidth: 480,
          margin: '0 0 16px',
        }}>
          We have sent a secure sign-in link to
        </p>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(18px, 1.6vw, 22px)',
          fontWeight: 600,
          color: P.INK,
          margin: '0 0 clamp(36px, 4vw, 56px)',
          letterSpacing: '-0.005em',
        }}>
          {email}
        </p>

        <div style={{
          width: '100%',
          maxWidth: 480,
          padding: 'clamp(20px, 2.2vw, 32px) clamp(20px, 2.2vw, 32px)',
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          borderLeft: '2px solid ' + P.GO,
          textAlign: 'left',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.9vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 12,
          }}>
            Next step
          </div>
          <p style={{
            ...T.bodyMd,
            color: P.TX,
            lineHeight: 1.75,
          }}>
            The link expires in 30 minutes. If you do not see it within a few minutes, check your spam folder. Clicking the link will sign you in automatically.
          </p>
        </div>
      </Shell>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // FORM STATE — request magic link
  // ═════════════════════════════════════════════════════════════════════
  var inputBase = {
    width: '100%',
    padding: 'clamp(14px, 1.4vw, 18px) clamp(16px, 1.6vw, 22px)',
    fontFamily: FF,
    fontSize: 'clamp(16px, 1.25vw, 18px)',
    fontWeight: 400,
    background: P.PAP,
    color: P.INK,
    border: '1px solid ' + P.SAND3,
    borderRadius: 0,
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
    transition: 'border-color 0.2s, opacity 0.2s',
    opacity: sending ? 0.6 : 1,
    cursor: sending ? 'default' : 'text',
  };
  var onInputFocus = function(e) { if (!sending) e.target.style.borderColor = P.BR; };
  var onInputBlur  = function(e) { e.target.style.borderColor = P.SAND3; };

  var labelStyle = {
    display: 'block',
    fontFamily: FF,
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: P.BR2,
    marginBottom: 10,
    textAlign: 'left',
  };

  return (
    <Shell>
      {/* Brand mark — ceremonial single-anchor ankh, top of column */}
      <div style={{ marginBottom: 'clamp(24px, 2.5vw, 36px)' }}>
        <Ankh responsive="opener" color={P.GO} />
      </div>

      {/* Title */}
      <h1 style={titleStyle}>
        <span style={{ display: 'block' }}>Welcome back.</span>
        <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>Sign in to continue.</span>
      </h1>
      <div style={accentRule} />

      {/* Body intro */}
      <p style={{
        ...T.bodyLg,
        color: P.TX,
        maxWidth: 460,
        margin: '0 0 clamp(36px, 4vw, 56px)',
      }}>
        Enter your email and we will send you a secure sign-in link. No password required.
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(18px, 1.8vw, 24px)',
      }}>
        <div>
          <label htmlFor="cs-magic-email" style={labelStyle}>Email address</label>
          <input
            id="cs-magic-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={function(e) { setEmail(e.target.value); }}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            placeholder="you@example.com"
            disabled={sending}
            style={inputBase}
          />
        </div>

        {error && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.15vw, 17px)',
            fontStyle: 'italic',
            color: P.RED,
            padding: '4px 2px',
          }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 'clamp(8px, 1vw, 16px)', textAlign: 'center' }}>
          <BtnPrimary onClick={handleSubmit} large disabled={sending}>
            {sending ? 'Sending…' : 'Send me a magic link'}
          </BtnPrimary>
        </div>
      </form>

      {/* New-user side action */}
      <p style={{
        marginTop: 'clamp(36px, 4vw, 56px)',
        fontFamily: FF,
        fontSize: 'clamp(14px, 1.1vw, 16px)',
        color: P.TL,
        lineHeight: 1.7,
      }}>
        New to Cleo's Secret?{' '}
        <span
          onClick={goIntake}
          style={{
            fontStyle: 'italic',
            color: P.BR2,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: '0.18em',
            fontSize: '1.05em',
          }}>
          Start your health conversation.
        </span>
      </p>
    </Shell>
  );
}
